import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { error } from "../../common/helpers/toastify";
import "./ImportFilesReplenishOrder.scss";

const ALLOWED_EXTENSIONS = [".csv", ".xls", ".xlsx"];
const ACCEPT_ATTR = ".csv,.xls,.xlsx";

const getFileExtension = (fileName = "") => {
  const idx = String(fileName).lastIndexOf(".");
  return idx >= 0 ? String(fileName).slice(idx).toLowerCase() : "";
};

const isAllowedImportFile = (file) => {
  if (!file?.name) return false;
  return ALLOWED_EXTENSIONS.includes(getFileExtension(file.name));
};

const formatFileSize = (bytes = 0) => {
  const size = Number(bytes) || 0;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const ImportFilesReplenishOrder = (props) => {
  const { t } = useTranslation();
  const { setImportfile, readExcel, importfile } = props;
  const [isDragging, setIsDragging] = useState(false);

  const applyFile = (file) => {
    if (!file) return;
    if (!isAllowedImportFile(file)) {
      error(t("Only CSV or XLS files are accepted."));
      return;
    }
    setImportfile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    applyFile(event.dataTransfer.files?.[0]);
  };

  return (
    <div
      className="modal fade import-modal"
      id="ReplenishOrderImportModal"
      tabIndex={-1}
      aria-labelledby="ImportModalLabel"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <div className="modal-header-text">
              <p className="modal-title">{t("Import Replenish Order")}</p>
              <p className="modal-sub-title">{t("Select a CSV or XLS file to import")}</p>
            </div>

            <button
              type="button"
              className="import-modal-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 11L6 6M6 6L1 1M6 6L11 1M6 6L1 11"
                  stroke="#A4A4A4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="modal-body">
            {!importfile?.name ? (
              <label
                className={`import-modal-choose-file${isDragging ? " is-dragging" : ""}`}
                htmlFor="upload-section-replenishOrder"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <span className="import-modal-icon-wrap">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.33327 23.9996C7.66964 23.9996 6.07415 23.3675 4.89779 22.2422C3.72142 21.117 3.06055 19.5909 3.06055 17.9996C3.06055 16.4083 3.72142 14.8822 4.89779 13.757C6.07415 12.6318 7.66964 11.9996 9.33327 11.9996C9.72619 10.2492 10.8756 8.71092 12.5287 7.72323C13.3473 7.23417 14.2648 6.89501 15.229 6.72509C16.1931 6.55518 17.1851 6.55785 18.1481 6.73294C19.1111 6.90804 20.0264 7.25213 20.8417 7.74558C21.6569 8.23903 22.3562 8.87217 22.8996 9.60885C23.443 10.3455 23.8199 11.1713 24.0087 12.0391C24.1975 12.9068 24.1945 13.7996 23.9999 14.6663H25.3333C26.571 14.6663 27.7579 15.1579 28.6331 16.0331C29.5083 16.9083 29.9999 18.0953 29.9999 19.3329C29.9999 20.5706 29.5083 21.7576 28.6331 22.6328C27.7579 23.5079 26.571 23.9996 25.3333 23.9996H23.9999"
                      stroke="#52CBC4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 20L16 16L20 20"
                      stroke="#52CBC4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 16V28"
                      stroke="#52CBC4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <p className="file-drop-title">
                  {t("Drop your document here or")}{" "}
                  <span className="browse-file-link">{t("browse file")}</span>
                </p>
                <p className="file-drop-sub-title">
                  {t("Maximum file size: 25 MB")}
                  <span className="file-drop-dot">·</span>
                  {t("File format: CSV or XLS")}
                </p>
                <input
                  id="upload-section-replenishOrder"
                  type="file"
                  accept={ACCEPT_ATTR}
                  onChange={(e) => {
                    applyFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                  name="files"
                />
              </label>
            ) : (
              <div className="import-modal-file-details-area w-100">
                <div className="import-modal-file-details-area-left">
                  <span className="import-modal-file-icon">
                    <svg
                      width="16"
                      height="20"
                      viewBox="0 0 16 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.5 19.1663H15.5V5.59051L10.7417 0.833008H0.5V19.1663ZM10.5 1.66634H10.6667L14.6667 5.66634V5.83301H10.5V1.66634ZM1.33333 1.66634H9.66667V6.66634H14.6667V18.333H1.33333V1.66634Z"
                        fill="#52CBC4"
                      />
                    </svg>
                  </span>
                  <div className="flexbox">
                    <p className="import-modal-file-details-area-name">
                      {importfile?.name}
                    </p>
                    <p className="import-modal-file-details-area-size">
                      {formatFileSize(importfile?.size)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="import-modal-remove-file"
                  onClick={() => setImportfile(null)}
                  aria-label={t("Remove file")}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.8335 3.83333L13.1107 13.9521C13.0484 14.8243 12.3227 15.5 11.4483 15.5H4.55203C3.67763 15.5 2.9519 14.8243 2.8896 13.9521L2.16683 3.83333M6.3335 7.16667V12.1667M9.66683 7.16667V12.1667M10.5002 3.83333V1.33333C10.5002 0.873096 10.1271 0.5 9.66683 0.5H6.3335C5.87326 0.5 5.50016 0.873096 5.50016 1.33333V3.83333M1.3335 3.83333H14.6668"
                      stroke="#FF5757"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}

            <div className="download-sample-panel download-sample-panel--text-only">
              <p className="download-sample-text">
                {t("Download a sample file and compare it with your import file before uploading.")}
              </p>
            </div>
          </div>

          {importfile?.name ? (
            <div id="clickonme" className="modal-footer">
              <button
                type="button"
                data-bs-dismiss="modal"
                className="btn modal-content-cancel"
                onClick={() => setImportfile(null)}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                onClick={() => {
                  readExcel();
                  setImportfile(null);
                }}
                data-bs-dismiss="modal"
                className="btn modal-content-yes"
              >
                {t("Submit")}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ImportFilesReplenishOrder;
